import './InputSettingsModal.scss';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Checkbox } from '@Components/FormCheck';
import { Button } from '../../../Components/Button/Button';
import { DatePicker } from '../../../Components/DatePicker';
import { SegmentationMappingLambdaFetch } from '../../../utils/fetch-utils';
import { useMap } from '../../../utils/hooks/useData';

// Redshift-table bounds. null on either endpoint means open-ended (null start => from the
// beginning, null end => through present). Shared by the save request body and the values
// read back from PG to prefill the pickers on open.
export interface ModelTimeline {
  startDate: string | null;
  endDate: string | null;
}

// The DatePicker and the start > end comparison both assume bare yyyy-MM-dd strings. On read,
// pg may hand back the whole modelTimeline as null (both endpoints unset), a partially-filled
// object, and either "2024-01-01" (text column) or a full ISO timestamp
// ("2024-01-01T00:00:00.000Z", when the column is a real date/timestamp type that node-postgres
// parses into a Date). Slice to the date portion so every form normalizes identically without
// reinterpreting the value through a timezone.
export const normalizeModelTimeline = (timeline: ModelTimeline | null): ModelTimeline => ({
  startDate: timeline?.startDate?.slice(0, 10) ?? null,
  endDate: timeline?.endDate?.slice(0, 10) ?? null
});

interface InputSettingsModalProps {
  show: boolean;
  onClose: () => void;
  company: string;
  existingInputSegments: number[];
  existingModelTimeline: ModelTimeline;
  segmentOptions: { value: number; name: string }[];
  setExistingInputSegments: Dispatch<SetStateAction<number[]>>;
  setExistingModelTimeline: Dispatch<SetStateAction<ModelTimeline>>;
  onJobStarted: () => void;
}

const InputSettingsModal = ({
  show,
  onClose,
  company,
  existingInputSegments,
  existingModelTimeline,
  segmentOptions,
  setExistingInputSegments,
  setExistingModelTimeline,
  onJobStarted
}: InputSettingsModalProps) => {
  // Initialize selected segments to the existing values fetched from PG.
  const [selectedInputSegments, setSelectedInputSegmentsValue] = useMap<string, boolean>(
    existingInputSegments.reduce(
      (acc, currentItem) => {
        acc[currentItem.toString()] = true;
        return acc;
      },
      {} as Record<string, boolean>
    )
  );

  // Model Timeline range sent to the backend to bound the redshift table. Empty string on either
  // endpoint means open-ended (blank start => from the beginning, blank end => through present).
  // Seeded from the existing timeline fetched from PG so the saved range shows on open.
  const [timelineStart, setTimelineStart] = useState(existingModelTimeline.startDate ?? '');
  const [timelineEnd, setTimelineEnd] = useState(existingModelTimeline.endDate ?? '');

  // ISO yyyy-MM-dd strings compare correctly as strings.
  const timelineInvalid =
    Boolean(timelineStart) && Boolean(timelineEnd) && timelineStart > timelineEnd;

  const handleSave = useCallback(async () => {
    const selectedSegmentIds = Object.keys(selectedInputSegments)
      .filter(segment => selectedInputSegments[segment])
      .map(segmentId => parseInt(segmentId, 10));

    const modelTimeline = {
      startDate: timelineStart || null,
      endDate: timelineEnd || null
    };

    await SegmentationMappingLambdaFetch('/saveFeatureMappingInputSegments', {
      method: 'POST',
      body: {
        company,
        segments: selectedSegmentIds,
        modelTimeline
      }
    });

    setExistingInputSegments(selectedSegmentIds);
    setExistingModelTimeline(modelTimeline);
    onClose();
    onJobStarted();
  }, [
    company,
    onClose,
    onJobStarted,
    selectedInputSegments,
    setExistingInputSegments,
    setExistingModelTimeline,
    timelineStart,
    timelineEnd
  ]);

  // Channel and Platform are input segments by default, so we want to auto-check and disable these.
  const isChannelOrPlatform = (value: number) => value === 1 || value === 2;

  return (
    <Modal show={show} onHide={onClose} centered className='input-settings-modal'>
      <Modal.Header className='input-settings-modal-header'>
        <Modal.Title className='input-settings-modal-title'>Select model inputs</Modal.Title>
      </Modal.Header>
      <Modal.Body className='input-settings-modal-body'>
        <div className='model-timeline-section'>
          <div className='section-label'>Model Timeline</div>
          <TimelineField
            label='Start'
            placeholder='Beginning'
            value={timelineStart}
            onChange={setTimelineStart}
          />
          <TimelineField
            label='End'
            placeholder='Present'
            value={timelineEnd}
            onChange={setTimelineEnd}
          />
          {timelineInvalid && (
            <div className='timeline-error'>Start date must be on or before end date.</div>
          )}
        </div>
        <div className='input-segments-section'>
          <div className='section-label'>Segments</div>
          {segmentOptions.map(segment => (
            <Checkbox
              key={segment.value}
              className='input-segment-check-box'
              checked={selectedInputSegments[segment.value] || false}
              disabled={isChannelOrPlatform(segment.value)}
              label={segment.name}
              onChange={() => {
                setSelectedInputSegmentsValue(
                  segment.value.toString(),
                  !selectedInputSegments[segment.value]
                );
              }}
            />
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleSave} disabled={timelineInvalid}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface TimelineFieldProps {
  label: string;
  placeholder: string;
  value: string;
  onChange: (date: string) => void;
}

const TimelineField = ({ label, placeholder, value, onChange }: TimelineFieldProps) => (
  <div className='timeline-field'>
    <span className='timeline-field-label'>{label}</span>
    <DatePicker
      isSingle
      isClearable
      placeholder={placeholder}
      range={{ start: value, end: value }}
      onChange={({ start }) => onChange(start)}
    />
  </div>
);

export { InputSettingsModal };
